import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { folderPaths, type ProjectTree } from '@gotti/sandbox-core';
import { ContextMenu } from './ContextMenu';
import { IconFolder } from '../agent/components/icons';
import { baseNameOf, folderOf } from './projectPaths';
import type { TreeActionResult } from './useProjectEditor';
import './FileTree.css';

interface FileTreeProps {
  tree: ProjectTree;
  errors: Record<string, string[]>;
  currentPath: string | null;
  expandedFolders: Set<string>;
  onToggleFolder: (folder: string) => void;
  onFileClick: (path: string) => void;
  /** The file tree's actions answer with what they did, or why they were refused — which the tree shows by the row. */
  onNewFile: (folder: string, name: string) => TreeActionResult;
  onNewFolder: (parent: string, name: string) => TreeActionResult;
  onDeleteFile: (path: string) => TreeActionResult;
  onRenameFile: (path: string, newName: string, options?: { updateImports?: boolean }) => TreeActionResult;
  /** Where a rename would take a file, or why it may not — asked before offering to rewrite its importers' imports. */
  onCheckRename: (path: string, newName: string) => TreeActionResult;
  onRenameFolder: (folder: string, newName: string) => TreeActionResult;
  onDeleteFolder: (folder: string) => TreeActionResult;
  onCopyFile: (path: string) => void;
  onCutFile: (path: string) => void;
  onPasteFile: (folder: string) => void;
  hasClipboard: () => boolean;
  hasImportReferences: (path: string) => boolean;
  canDelete: (path: string) => boolean;
  /** Whether a file may be renamed: the files the project cannot run without may not, so no rename is offered for them. */
  canRename: (path: string) => boolean;
  /** Whether a folder may be deleted or renamed: not one holding a file the project cannot run without (each side, the project). */
  canDeleteFolder: (folder: string) => boolean;
  canRenameFolder: (folder: string) => boolean;
}

interface InlineEditInputProps {
  initialValue: string;
  /** The reason a name was refused before this input opened (a rename refused after its imports were asked about). */
  initialError?: string | null;
  /**
   * Takes the name typed (never empty — nothing typed cancels): `null` when it was taken (the input closes), else the
   * reason it was refused, shown under the input, which stays open with the name.
   */
  onSubmit: (value: string) => string | null;
  onCancel: () => void;
  placeholder?: string;
}

/** A name being typed in the file tree, Enter or a blur to take it, Escape to cancel; a refused name says why under it. */
function InlineEditInput({ initialValue, initialError = null, onSubmit, onCancel, placeholder }: InlineEditInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(initialError);

  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const submit = () => {
    if (!value.trim()) {
      onCancel();
      return;
    }
    const refusal = onSubmit(value);
    if (refusal !== null) setError(refusal);
  };
  const handleBlur = () => submit();
  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  return (
    <span className="inline-edit" onClick={(e) => e.stopPropagation()}>
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => { setValue(e.target.value); setError(null); }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      placeholder={placeholder}
      className="inline-edit-input"
      data-testid="tree-inline-input"
      style={{
        background: '#1e1e1e',
        border: '1px solid #007acc',
        color: '#d4d4d4',
        fontSize: '12px',
        padding: '1px 4px',
        width: '100%',
        outline: 'none',
      }}
    />
    {error !== null && <span className="inline-edit-error" data-testid="tree-inline-error" role="alert">{error}</span>}
    </span>
  );
}

/** Why an action on a row without an input (a delete) was refused, shown under the row. */
function RowError({ reason, paddingLeft }: { reason: string; paddingLeft: number }) {
  return <div className="inline-edit-error tree-row-error" data-testid="tree-inline-error" role="alert" style={{ paddingLeft: `${paddingLeft}px` }}>{reason}</div>;
}

interface FileItemProps {
  path: string;
  errors: string[] | undefined;
  isActive: boolean;   // Currently open/focused in editor
  isSelected: boolean; // Selected in file tree (for keyboard operations)
  paddingLeft: number;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  /** The rename being typed for this file, if any: the name it opens with and a reason given before it opened. */
  renaming: { value: string; error: string | null } | null;
  onRename: (newName: string) => string | null;
  onCancelRename: () => void;
  onQuickRename: () => void;
  onQuickDelete: () => void;
  canDelete: boolean;
  canRename: boolean;
}

/** The file's kind on its icon: `TS` for TypeScript, as before, else its extension. */
const iconText = (name: string): string => (/\.tsx?$/.test(name) ? 'TS' : (/\.([A-Za-z0-9]+)$/.exec(name)?.[1] ?? '').toUpperCase());

function FileItem({ path, errors, isActive, isSelected, paddingLeft, onClick, onContextMenu, renaming, onRename, onCancelRename, onQuickRename, onQuickDelete, canDelete, canRename }: FileItemProps) {
  const isRenaming = renaming !== null;
  const name = baseNameOf(path);
  const hasErrors = !!errors && errors.length > 0;
  const tooltip = hasErrors ? errors!.join('\n') : undefined;
  const classNames = ['file-tree-file', isActive ? 'active' : '', isSelected ? 'selected' : '', hasErrors ? 'has-errors' : '', isRenaming ? 'editing' : ''].filter(Boolean).join(' ');
  return (
    <div className={classNames} data-testid={`tree-file:${path}`} style={{ paddingLeft: `${paddingLeft}px` }} onClick={onClick} onContextMenu={onContextMenu} title={tooltip}>
      <span className="file-icon">{iconText(name)}</span>
      <span className={`file-name${isRenaming ? ' editing' : ''}`}>
        {isRenaming ? (
          <InlineEditInput initialValue={renaming.value} initialError={renaming.error} onSubmit={onRename} onCancel={onCancelRename} />
        ) : (
          name
        )}
      </span>
      {!isRenaming && (
        <span className="file-actions">
          {canRename && (
            <button className="file-action-btn rename" data-testid={`tree-rename:${path}`} title="Rename" onClick={(e) => { e.stopPropagation(); onQuickRename(); }}>✎</button>
          )}
          {canDelete && (
            <button className="file-action-btn delete" data-testid={`tree-delete:${path}`} title="Delete" onClick={(e) => { e.stopPropagation(); onQuickDelete(); }}>✖</button>
          )}
        </span>
      )}
    </div>
  );
}

interface FolderHeaderProps {
  /** The folder's project path (`''` is the project root): what its row and actions are marked by. */
  path: string;
  label: string;
  isExpanded: boolean;
  paddingLeft: number;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onAddFile?: () => void;
  onAddFolder?: () => void;
  /** Offered for a folder that may be renamed or deleted. */
  onRename?: () => void;
  onDelete?: () => void;
  /** The folder's new name being typed, in place of its label. */
  renaming?: { onSubmit: (value: string) => string | null; onCancel: () => void };
  isRoot?: boolean;
  hasDescendantErrors?: boolean;
}

function FolderHeader({ path, label, isExpanded, paddingLeft, onClick, onContextMenu, onAddFile, onAddFolder, onRename, onDelete, renaming, isRoot, hasDescendantErrors }: FolderHeaderProps) {
  const classNames = [
    'file-tree-folder',
    isRoot ? 'root-folder' : '',
    isExpanded ? 'expanded' : 'collapsed',
    hasDescendantErrors ? 'has-errors' : '',
    renaming ? 'editing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      data-testid={`tree-folder:${path}`}
      style={{ paddingLeft: `${paddingLeft}px` }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('.folder-actions, .inline-edit')) return;
        onClick();
      }}
      onContextMenu={onContextMenu}
    >
      <span className="folder-toggle">{isExpanded ? '▼' : '▶'}</span>
      {renaming ? (
        <span className="folder-label editing"><IconFolder /> <InlineEditInput initialValue={label} onSubmit={renaming.onSubmit} onCancel={renaming.onCancel} /></span>
      ) : (
        <span className="folder-label"><IconFolder /> {label}</span>
      )}
      {!renaming && (onAddFile || onAddFolder || onRename || onDelete) && (
        <span className="folder-actions">
          {onAddFile && (
            <button
              className="tree-action-btn"
              data-testid={`tree-new-file:${path}`}
              onClick={(e) => { e.stopPropagation(); onAddFile(); }}
              title="New file"
            >
              +
            </button>
          )}
          {onAddFolder && (
            <button
              className="tree-action-btn"
              data-testid={`tree-new-folder:${path}`}
              onClick={(e) => { e.stopPropagation(); onAddFolder(); }}
              title="New folder"
            >
              <IconFolder />
            </button>
          )}
          {onRename && (
            <button
              className="tree-action-btn rename"
              data-testid={`tree-rename:${path}`}
              onClick={(e) => { e.stopPropagation(); onRename(); }}
              title="Rename folder"
            >
              ✎
            </button>
          )}
          {onDelete && (
            <button
              className="tree-action-btn delete"
              data-testid={`tree-delete:${path}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="Delete folder"
            >
              ✖
            </button>
          )}
        </span>
      )}
    </div>
  );
}

interface PendingNew {
  type: 'file' | 'folder';
  /** The folder the new file or folder goes in (`''` is the project root). */
  folder: string;
}

/** The three sides, shown as the tree's first root folders as they always were. */
const SIDES = ['shared', 'client', 'server'] as const;
const sideRoots: readonly string[] = SIDES.map((side) => `src/${side}`);
/** Conventional folders first, then the rest alphabetically — as before. */
const CONVENTIONAL = ['systems', 'components', 'entities'];
const byConvention = (a: string, b: string) => {
  const [aName, bName] = [baseNameOf(a), baseNameOf(b)];
  const [aAt, bAt] = [CONVENTIONAL.indexOf(aName), CONVENTIONAL.indexOf(bName)];
  if (aAt !== -1 && bAt !== -1) return aAt - bAt;
  if (aAt !== -1) return -1;
  if (bAt !== -1) return 1;
  return aName.localeCompare(bName);
};
const isTestFile = (path: string) => /\.(spec|test)\.tsx?$/.test(path);

export function FileTree({
  tree, errors, currentPath, expandedFolders, onToggleFolder, onFileClick, onNewFile, onNewFolder, onDeleteFile,
  onRenameFile, onCheckRename, onRenameFolder, onDeleteFolder, onCopyFile, onCutFile, onPasteFile, hasClipboard,
  hasImportReferences, canDelete, canRename, canDeleteFolder, canRenameFolder,
}: FileTreeProps) {
  const treeRef = useRef<HTMLDivElement>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; type: 'file' | 'folder'; path: string } | null>(null);
  /** The file whose new name is being typed: the name the input opens with, and a reason given before it opened. */
  const [renaming, setRenaming] = useState<{ path: string; value: string; error: string | null } | null>(null);
  /** The folder whose new name is being typed. */
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  /** Why the last delete was refused, shown under the row it was asked of until the next action. */
  const [rowError, setRowError] = useState<{ path: string; reason: string } | null>(null);
  const [pendingNew, setPendingNew] = useState<PendingNew | null>(null);
  const [renamePrompt, setRenamePrompt] = useState<{ path: string; newName: string } | null>(null);
  const [alwaysUpdateImports, setAlwaysUpdateImports] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sandbox-always-update-imports') === 'true';
    } catch {
      return false;
    }
  });

  /** Each folder's own files (tree order) and child folders (conventional order). */
  const index = useMemo(() => {
    const files = new Map<string, string[]>();
    const folders = new Map<string, string[]>();
    for (const folder of ['', ...folderPaths(tree)]) { files.set(folder, []); folders.set(folder, []); }
    for (const folder of folderPaths(tree)) folders.get(folderOf(folder))!.push(folder);
    for (const path of Object.keys(tree.files)) if (!isTestFile(path)) files.get(folderOf(path))!.push(path);
    for (const list of folders.values()) list.sort(byConvention);
    return { files, folders };
  }, [tree]);

  const hasErrorsUnder = (folder: string) =>
    Object.entries(errors).some(([path, list]) => list.length > 0 && (folder === '' || path.startsWith(`${folder}/`)));

  // Handle keyboard shortcuts for cut/copy/paste when file tree has focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!treeRef.current?.contains(document.activeElement)) return;
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) return;
      const target = selectedPath ?? currentPath;
      if (!target) return;
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (cmdOrCtrl && !e.shiftKey && !e.altKey) {
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          onCopyFile(target);
        } else if (e.key === 'x' || e.key === 'X') {
          e.preventDefault();
          if (canDelete(target)) onCutFile(target);
        } else if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          if (hasClipboard()) onPasteFile(folderOf(target));
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          if (canDelete(target)) deleteFile(target);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPath, currentPath, onCopyFile, onCutFile, onPasteFile, onDeleteFile, hasClipboard, canDelete]);

  // Clear selection when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (treeRef.current && !treeRef.current.contains(e.target as Node)) setSelectedPath(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const openContextMenu = (e: React.MouseEvent, type: 'file' | 'folder', path: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type, path });
  };

  /**
   * A new name typed for a file: refused → its reason, shown under the input; else the file is renamed — after asking
   * whether to rewrite its importers' imports, when files import it — and the input closes.
   */
  const handleRename = (path: string, newName: string): string | null => {
    const check = onCheckRename(path, newName);
    if (!check.ok) return check.reason;
    if (check.path !== path) {
      const hasRefs = hasImportReferences(path);
      if (hasRefs && !alwaysUpdateImports) {
        setRenamePrompt({ path, newName });
      } else {
        const renamed = onRenameFile(path, newName, { updateImports: hasRefs });
        if (!renamed.ok) return renamed.reason;
      }
    }
    setRenaming(null);
    return null;
  };
  const handleFolderRename = (folder: string, newName: string): string | null => {
    const renamed = onRenameFolder(folder, newName);
    if (!renamed.ok) return renamed.reason;
    setRenamingFolder(null);
    return null;
  };
  const deleteFile = (path: string) => {
    const deleted = onDeleteFile(path);
    setRowError(deleted.ok ? null : { path, reason: deleted.reason });
  };
  const deleteFolder = (folder: string) => {
    const deleted = onDeleteFolder(folder);
    setRowError(deleted.ok ? null : { path: folder, reason: deleted.reason });
    closeContextMenu();
  };

  const startRename = (path: string) => {
    setRenaming({ path, value: baseNameOf(path).replace(/\.tsx?$/, ''), error: null });
    setRowError(null);
    closeContextMenu();
  };
  const startFolderRename = (folder: string) => { setRenamingFolder(folder); setRowError(null); closeContextMenu(); };
  const startNew = (type: 'file' | 'folder', folder: string) => { setPendingNew({ type, folder }); setRowError(null); closeContextMenu(); };
  /** The name typed for a new file or folder: refused → its reason (the input stays); else the input closes. */
  const confirmAdd = (name: string): string | null => {
    if (!pendingNew) return null;
    const made = pendingNew.type === 'file' ? onNewFile(pendingNew.folder, name.trim()) : onNewFolder(pendingNew.folder, name.trim());
    if (!made.ok) return made.reason;
    setPendingNew(null);
    return null;
  };

  const persistAlwaysUpdateImports = (value: boolean) => {
    setAlwaysUpdateImports(value);
    try {
      localStorage.setItem('sandbox-always-update-imports', value ? 'true' : 'false');
    } catch {
      // ignore storage errors
    }
  };
  const confirmRenamePrompt = (updateImports: boolean) => {
    if (!renamePrompt) return;
    const renamed = onRenameFile(renamePrompt.path, renamePrompt.newName, { updateImports });
    setRenamePrompt(null);
    // Refused after all (the project changed while the question was open): the name goes back to its input, with why.
    if (!renamed.ok) setRenaming({ path: renamePrompt.path, value: renamePrompt.newName, error: renamed.reason });
  };

  const handleFileItemClick = (path: string) => {
    setSelectedPath(path);
    onFileClick(path);
    treeRef.current?.focus();
  };

  const pendingInput = (folder: string, paddingLeft: number) => {
    if (!pendingNew || pendingNew.folder !== folder) return null;
    return pendingNew.type === 'file' ? (
      <div className="file-tree-file editing" style={{ paddingLeft: `${paddingLeft}px` }}>
        <span className="file-icon">TS</span>
        <span className="file-name editing">
          <InlineEditInput initialValue="" onSubmit={confirmAdd} onCancel={() => setPendingNew(null)} placeholder="filename" />
        </span>
      </div>
    ) : (
      <div className="file-tree-folder editing" style={{ paddingLeft: `${paddingLeft}px` }}>
        <span className="folder-toggle">▶</span>
        <span className="folder-label"><IconFolder /> </span>
        <InlineEditInput initialValue="" onSubmit={confirmAdd} onCancel={() => setPendingNew(null)} placeholder="folder name" />
      </div>
    );
  };

  const renderFiles = (paths: string[], paddingLeft: number) => paths.map((path) => (
    <React.Fragment key={path}>
    <FileItem
      path={path}
      errors={errors[path]}
      isActive={currentPath === path}
      isSelected={selectedPath === path}
      paddingLeft={paddingLeft}
      onClick={() => handleFileItemClick(path)}
      onContextMenu={(e) => openContextMenu(e, 'file', path)}
      renaming={renaming?.path === path ? renaming : null}
      onRename={(newName) => handleRename(path, newName)}
      onCancelRename={() => setRenaming(null)}
      onQuickRename={() => startRename(path)}
      onQuickDelete={() => deleteFile(path)}
      canDelete={canDelete(path)}
      canRename={canRename(path)}
    />
    {rowError?.path === path && <RowError reason={rowError.reason} paddingLeft={paddingLeft} />}
    </React.Fragment>
  ));

  /** A folder below a root folder, at any depth: its header, then its files and folders when it is open. */
  const renderFolder = (folder: string, depth: number): React.ReactNode => {
    const isExpanded = expandedFolders.has(folder);
    const padding = 4 + depth * 12;
    return (
      <React.Fragment key={folder}>
        <FolderHeader
          path={folder}
          label={baseNameOf(folder)}
          isExpanded={isExpanded}
          paddingLeft={padding}
          onClick={() => onToggleFolder(folder)}
          onContextMenu={(e) => openContextMenu(e, 'folder', folder)}
          onAddFile={() => startNew('file', folder)}
          onAddFolder={() => startNew('folder', folder)}
          onRename={canRenameFolder(folder) ? () => startFolderRename(folder) : undefined}
          onDelete={canDeleteFolder(folder) ? () => deleteFolder(folder) : undefined}
          renaming={renamingFolder === folder ? { onSubmit: (value) => handleFolderRename(folder, value), onCancel: () => setRenamingFolder(null) } : undefined}
          hasDescendantErrors={hasErrorsUnder(folder)}
        />
        {rowError?.path === folder && <RowError reason={rowError.reason} paddingLeft={padding + 12} />}
        {isExpanded && (
          <div className="file-tree-files">
            {renderFiles(index.files.get(folder) ?? [], padding + 12)}
            {pendingInput(folder, padding + 12)}
            {(index.folders.get(folder) ?? [])
              // Under `project`, `src` holds only what is not a side: the sides are root folders of their own.
              .filter((child) => !(folder === 'src' && sideRoots.includes(child)))
              .map((child) => renderFolder(child, depth + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  /** A root folder — a side (`src/<side>`) or the project — with its root files, then its folders. */
  const renderRoot = (label: string, folder: string, childFolders: string[], rootFiles: string[]) => {
    const isExpanded = expandedFolders.has(folder);
    return (
      <React.Fragment key={label}>
        <FolderHeader
          path={folder}
          label={label}
          isExpanded={isExpanded}
          paddingLeft={4}
          onClick={() => onToggleFolder(folder)}
          onContextMenu={(e) => openContextMenu(e, 'folder', folder)}
          onAddFile={() => startNew('file', folder)}
          onAddFolder={() => startNew('folder', folder)}
          isRoot
          hasDescendantErrors={hasErrorsUnder(folder)}
        />
        {isExpanded && (
          <div className="file-tree-children">
            {renderFiles(rootFiles, 16)}
            {pendingInput(folder, 16)}
            {childFolders.map((child) => renderFolder(child, 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  // The project root folder: the root files and every top-level folder — `src` only when it holds more than the sides.
  const srcHasMore = (index.files.get('src') ?? []).length > 0 || (index.folders.get('src') ?? []).some((child) => !sideRoots.includes(child));
  const projectFolders = (index.folders.get('') ?? []).filter((folder) => folder !== 'src' || srcHasMore);

  const getContextMenuItems = () => {
    if (!contextMenu) return [];
    if (contextMenu.type === 'file') {
      const removable = canDelete(contextMenu.path);
      return [
        ...(canRename(contextMenu.path) ? [{ id: 'rename', label: 'Rename', action: () => startRename(contextMenu.path) }] : []),
        ...(removable ? [{ id: 'cut', label: 'Cut', action: () => { onCutFile(contextMenu.path); closeContextMenu(); } }] : []),
        { id: 'copy', label: 'Copy', action: () => { onCopyFile(contextMenu.path); closeContextMenu(); } },
        ...(removable ? [{ id: 'delete', label: 'Delete', action: () => { deleteFile(contextMenu.path); closeContextMenu(); }, danger: true }] : []),
      ];
    }
    return [
      { id: 'new-file', label: 'New File', action: () => startNew('file', contextMenu.path) },
      ...(hasClipboard() ? [{ id: 'paste', label: 'Paste', action: () => { onPasteFile(contextMenu.path); closeContextMenu(); } }] : []),
      { id: 'new-folder', label: 'New Folder', action: () => startNew('folder', contextMenu.path) },
      ...(canRenameFolder(contextMenu.path) ? [{ id: 'rename-folder', label: 'Rename Folder', action: () => startFolderRename(contextMenu.path) }] : []),
      ...(canDeleteFolder(contextMenu.path) ? [{ id: 'delete-folder', label: 'Delete Folder', action: () => deleteFolder(contextMenu.path), danger: true }] : []),
    ];
  };

  return (
    <div ref={treeRef} className="file-tree" id="file-tree" data-testid="file-tree" tabIndex={0}>
      {SIDES.map((side) =>
        renderRoot(side, `src/${side}`, index.folders.get(`src/${side}`) ?? [], index.files.get(`src/${side}`) ?? []))}
      {renderRoot('project', '', projectFolders, index.files.get('') ?? [])}

      {contextMenu && <ContextMenu x={contextMenu.x} y={contextMenu.y} onClose={closeContextMenu} items={getContextMenuItems()} />}

      {renamePrompt && (
        <div className="rename-imports-modal" data-testid="rename-imports-modal">
          <div className="rename-imports-modal-content" role="dialog" aria-label="Update imports?">
            <h3>Update imports?</h3>
            <p>Do you want to update all import references to match the new file name?</p>
            <label className="rename-imports-checkbox">
              <input type="checkbox" checked={alwaysUpdateImports} onChange={(e) => persistAlwaysUpdateImports(e.target.checked)} />
              Always update imports
            </label>
            <div className="rename-imports-actions">
              <button className="btn btn-secondary" data-testid="rename-imports-no" onClick={() => confirmRenamePrompt(false)}>No</button>
              <button className="btn btn-primary" data-testid="rename-imports-yes" onClick={() => confirmRenamePrompt(true)}>Yes</button>
              <button className="btn btn-secondary" data-testid="rename-imports-cancel" onClick={() => setRenamePrompt(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
